import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router";
import {
    Button,
    ButtonToolbar,
    Input,
    Message,
    Nav,
    Panel,
    Popover,
    SelectPicker,
    Stack,
    Table,
    Tag,
    Toggle,
    Tooltip,
    useToaster,
    Whisper
} from "rsuite";
import { MappingType } from "../../core/types/GenericRelationTypes";
import { MethodsUtils } from "../../core/utils/MethodsUtils";
import axios from "../../utilities/axios";
import { notify } from "../../utilities/notify";
import styles from "./RelationPage.module.scss";
import { NoCompanySelected } from "../../components/NoCompanySelected";

const { Column, HeaderCell, Cell } = Table;

interface DeParaRow {
    id: string | number;
    enabled: boolean;
    omieCode: string;
    omieName: string;
    internalValue: string | null;
    paymentMethod?: string;
    note?: string;
}

function RelationPage() {
    const [active, setActive] = useState<MappingType>("documentType");
    const [selectData, setSelectData] = useState<{
        documentTypes: any[];
        paymentMethods: any[];
        checkingAccounts: any[];
        projects: any[];
        chartOfAccounts: any[];
        costCenter: any[];
    }>({
        documentTypes: [],
        paymentMethods: [],
        checkingAccounts: [],
        projects: [],
        chartOfAccounts: [],
        costCenter: []
    });
    const [data, setData] = useState<Record<MappingType, DeParaRow[]>>({
        documentType: [],
        paymentMethod: [],
        checkingAccount: [],
        project: [],
        chartOfAccounts: [],
        costCenter: []
    });
    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(false);

    //Select de Empresa
    const [companiesSelectData, setCompaniesSelectData] = useState<{ value: number, label: string }[]>([])
    const [selectedCompany, setSelectedCompany] = useState<number | string | null>(null)

    //Estados plano de contas
    const [activeCoA, setActiveCoA] = useState<"chartOfAccounts">("chartOfAccounts");
    const [selectDataCoA, setSelectDataCoA] = useState<{ chartOfAccounts: any[] }>({ chartOfAccounts: [] });
    const [dataCoA, setDataCoA] = useState<DeParaRow[]>([]);
    const [dirtyCoA, setDirtyCoA] = useState(false);
    const [loadingCoA, setLoadingCoA] = useState(false);


    const toaster = useToaster();

    const optionsCoA = useMemo(
        () =>
            MethodsUtils.toOptions(
                selectDataCoA.chartOfAccounts,
                (i: any) => i.nome ?? i.name ?? i.label ?? i.codigo ?? i.code,
                (i: any) => i.codigo ?? i.code ?? i.id ?? i.value
            ),
        [selectDataCoA]
    );

    useEffect(() => {
        loadCompanies()
    }, [])

    useEffect(() => {
        loadChartOfAccountsOptions();
        loadRelationsDataCoA();
    }, []);

    const isPersisted = (id: string | number | undefined) =>
        id !== undefined && id !== null && !Number.isNaN(Number(id));

    const loadChartOfAccountsOptions = async () => {
        try {
            setLoadingCoA(true);

            const { data } = await axios.get("/api/v1/import/omie/mappings/chartOfAccounts");

            if (Array.isArray(data)) {
                setSelectDataCoA({
                    chartOfAccounts: data.map((item: any) => ({
                        value: item.codigo,
                        label: item.nome
                    }))
                });
            }
        } catch {
            notify("Erro ao carregar Plano de Contas do Omie", "error");
        } finally {
            setLoadingCoA(false);
        }
    };

    const loadRelationsDataCoA = async () => {
        try {
            setLoadingCoA(true);
            const res = await axios.get("/api/v1/import/relations/chartOfAccounts/getAll");

            if (res.status === 200) {
                setDataCoA(res.data.data ?? []);
            }
        } catch {
            notify("Erro ao carregar relações de Plano de Contas", "error");
        } finally {
            setLoadingCoA(false);
        }
    };

    const handleChangeRowCoA = (id: string | number, patch: Partial<DeParaRow>) => {
        setDataCoA(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
        setDirtyCoA(true);
    };

    const handleAddRowCoA = () => {
        const newRow: DeParaRow = {
            id: MethodsUtils.generateUid(),
            enabled: true,
            omieCode: "",
            omieName: "",
            internalValue: null,
            note: ""
        };
        setDataCoA(prev => (Array.isArray(prev) ? [newRow, ...prev] : [newRow]));
        setDirtyCoA(true);
    };

    const handleRemoveRowCoA = (id: string | number) => {
        setDataCoA(prev => prev.filter(r => r.id !== id));
        setDirtyCoA(true);
    };

    const canDeleteRowCoA = (row: DeParaRow) => !isPersisted(row.id);

    const showActionsColumnCoA = Array.isArray(dataCoA) ? dataCoA.some(canDeleteRowCoA) : false;

    const handleSaveCoA = async () => {
        try {
            setLoadingCoA(true);

            for (const item of dataCoA) {
                if (!item?.omieName || !item?.internalValue) {
                    notify("Preencha todos os campos de De/Para em Plano de Contas", "info", 5500);
                    return;
                }
            }

            const response = await axios.post("/api/v1/import/relations/save", {
                data: dataCoA
            });

            if (response.status === 200) {
                toaster.push(
                    <Message type="success" closable>
                        Mapeamentos de Plano de Contas salvos com sucesso.
                    </Message>,
                    { placement: "topCenter" }
                );
                setDirtyCoA(false);
                await loadRelationsDataCoA();
            }
        } catch {
            notify("Erro ao salvar mapeamentos de Plano de Contas", "error");
        } finally {
            setLoadingCoA(false);
        }
    };

    const loadOmieMappings = async (integration_id: number | string | null) => {
        try {
            if (integration_id === null) return

            setLoading(true);

            const { data } = await axios.get("/api/v1/import/omie/mappings", {
                params: { integration_id: integration_id },
            });

            console.log({ data })

            setSelectData(prev => {
                const next = { ...prev };

                if (Array.isArray(data?.checkingAccounts)) {
                    next.checkingAccounts = data.checkingAccounts.map((item: any) => ({
                        value: item.codigo,
                        label: item.nome,
                    }));
                }

                if (Array.isArray(data?.paymentMethods)) {
                    next.paymentMethods = data.paymentMethods.map((item: any) => ({
                        value: item.codigo,
                        label: item.descricao
                    }))
                }

                if (Array.isArray(data?.documentTypes)) {
                    next.documentTypes = data.documentTypes.map((item: any) => ({
                        value: item.codigo,
                        label: item.descricao,
                    }));
                }

                if (Array.isArray(data?.projects)) {
                    next.projects = data.projects.map((item: any) => ({
                        value: item.codigo,
                        label: item.nome,
                    }));
                }

                // if (Array.isArray(data?.chartOfAccounts)) {
                //     next.chartOfAccounts = data.chartOfAccounts.map((item: any) => ({
                //         value: item.codigo,
                //         label: item.nome,
                //     }));
                // }

                if (Array.isArray(data?.costCenter)) {
                    next.costCenter = data.costCenter.map((item: any) => ({
                        value: item.codigo,
                        label: item.nome,
                    }));
                }

                return next;
            });
        } catch (error) {
            console.log("Erro ao carregar mapeamentos do Omie", error);
            notify("Erro ao carregar mapeamentos do Omie", "error");
        } finally {
            setLoading(false);
        }
    };

    const options = useMemo(() => {
        switch (active) {
            case "documentType":
                return MethodsUtils.toOptions(
                    selectData.documentTypes,
                    (i: any) => i.descricao ?? i.name ?? i.label ?? i.codigo ?? i.code,
                    (i: any) => i.codigo ?? i.code ?? i.id ?? i.value
                );

            case "paymentMethod":
                return MethodsUtils.toOptions(
                    selectData.paymentMethods,
                    (i: any) => i.descricao ?? i.name ?? i.label ?? i.codigo ?? i.code,
                    (i: any) => i.codigo ?? i.code ?? i.id ?? i.value
                );

            case "checkingAccount":
                return MethodsUtils.toOptions(
                    selectData.checkingAccounts,
                    (i: any) => i.nome ?? i.name ?? i.label ?? i.codigo ?? i.code,
                    (i: any) => i.codigo ?? i.code ?? i.id ?? i.value
                );

            case "project":
                return MethodsUtils.toOptions(
                    selectData.projects,
                    (i: any) => i.nome ?? i.name ?? i.label ?? i.codigo ?? i.code,
                    (i: any) => i.codigo ?? i.code ?? i.id ?? i.value
                );

            case "chartOfAccounts":
                return MethodsUtils.toOptions(
                    selectData.chartOfAccounts,
                    (i: any) => i.nome ?? i.name ?? i.label ?? i.codigo ?? i.code,
                    (i: any) => i.codigo ?? i.code ?? i.id ?? i.value
                );

            case "costCenter":
                return MethodsUtils.toOptions(
                    selectData.costCenter,
                    (i: any) => i.nome ?? i.name ?? i.label ?? i.codigo ?? i.code,
                    (i: any) => i.codigo ?? i.code ?? i.id ?? i.value
                );

            default:
                return [];
        }
    }, [active, selectData]);

    const loadCompanies = async () => {
        try {
            const { data } = await axios.get("/api/v1/integration/getAllActives")

            if (data?.data?.length === 0) return

            const formattedData = data?.data.map((company: any) => {
                return {
                    value: company.integration_id,
                    label: `${company.integration_company_cnpj} - ${company.integration_name}`
                }
            })

            setCompaniesSelectData(formattedData)
        } catch (error) {
            console.log("Erro ao carregar empresas", error)
            notify("Não foi possível carregar empresas", "warning");
        }
    }

    const loadRelationsData = async (integration_id: number | string | null) => {
        try {
            setLoading(true)

            if (!integration_id) return

            const res = await axios.get("/api/v1/import/relations/getAll", {
                params: {
                    integration_id: integration_id
                }
            })

            if (res.status === 200) {
                setData(res.data.data)
            }
        } catch (error) {
            console.log("Erro ao carregar relações", error)
            notify("Erro ao carregar relações", "error")
        } finally {
            setLoading(false)
        }
    }

    const handleChangeRow = (id: string | number, patch: Partial<DeParaRow>, isPaymentMethod?: boolean) => {
        if (isPaymentMethod) {
            if (patch.paymentMethod !== "TRA") {
                patch.internalValue = null
            }
        }
        setData(prev => {
            const next = { ...prev };
            next[active] = next[active].map(r => (r.id === id ? { ...r, ...patch } : r));
            return next;
        });
        setDirty(true);
    };

    const handleAddRow = () => {
        const newRow: DeParaRow = {
            id: MethodsUtils.generateUid(),
            enabled: true,
            omieCode: "",
            omieName: "",
            internalValue: null,
            note: ""
        };
        setData(prev => {
            const next = { ...prev };

            next[active] = [newRow, ...next[active]];
            return next;
        });
        setDirty(true);
    };

    const handleRemoveRow = (id: string | number) => {
        setData(prev => {
            const next = { ...prev };
            next[active] = next[active].filter(r => r.id !== id);
            return next;
        });
        setDirty(true);
    };

    const handleSave = async () => {
        try {
            setLoading(true)

            if (!selectedCompany) return

            const { costCenter, checkingAccount, chartOfAccounts, documentType, paymentMethod, project } = data;

            for (const item of costCenter) {
                if (!item?.omieName || !item?.internalValue) {
                    notify("Preencha todos os campos de De/Para na aba de Centro de Custo")
                    return
                }
            }

            for (const item of checkingAccount) {
                if (!item?.omieName || !item?.internalValue) {
                    notify("Preencha todos os campos de De/Para na aba de Contas Correntes")
                    return
                }
            }

            for (const item of chartOfAccounts) {
                if (!item?.omieName || !item?.internalValue) {
                    notify("Preencha todos os campos de De/Para na aba de Plano de Contas", "info", 5500)
                    return
                }
            }

            for (const item of documentType) {
                if (!item?.omieName || !item?.internalValue) {
                    notify("Preencha todos os campos de De/Para na aba de Tipos de Documento", "info", 5500)
                    return
                }
            }

            for (const item of project) {
                if (!item?.omieName || !item?.internalValue) {
                    notify("Preencha todos os campos de De/Para na aba de Projetos", "info", 5500)
                    return
                }
            }

            for (const item of paymentMethod) {
                if (!item?.omieName || !item?.paymentMethod) {
                    notify("Preencha todos os campos de De/Para na aba de Formas de Pagamento", "info", 5500)
                    return
                }

                if (item.paymentMethod === "TRA") {
                    if (!item.internalValue) {
                        notify("Preencha todos os campos de De/Para na aba de Formas de Pagamento", "info", 5500)
                        return
                    }
                }
            }

            const response = await axios.post("/api/v1/import/relations/save", {
                data,
                integration_id: selectedCompany
            })

            if (response.status === 200) {
                toaster.push(
                    <Message type="success" closable>
                        Mapeamentos salvos com sucesso.
                    </Message>,
                    { placement: "topCenter" }
                );
                setDirty(false);
                await loadRelationsData(selectedCompany);
            }
        } catch (error) {
            console.error(error)
            notify("Erro ao salvar mapeamentos", "error")
        } finally {
            setLoading(false)
        }
    }

    const handleChangeCompany = async (v: number | string | null) => {
        try {
            setSelectedCompany(v)
            loadOmieMappings(v)
            loadRelationsData(v)
            setDirty(false)
        } catch (error) {
            console.error(error)
            notify("Erro ao carregar empresas", "error")
        }
    }

    const NavTitle = ({
        title,
        subtitle
    }: {
        title: string;
        subtitle?: string;
    }) => (
        <div className={styles.navTitle}>
            <Stack spacing={8} alignItems="center">
                <h4 style={{ margin: 0 }}>{title}</h4>
                {subtitle ? <Tag>{subtitle}</Tag> : null}
                {dirty ? <Tag color="orange">Alterações não salvas</Tag> : null}
            </Stack>

            <SelectPicker
                data={companiesSelectData}
                value={selectedCompany}
                onChange={(v) => handleChangeCompany(v)}
                placeholder="Selecione uma Empresa"
                placement="leftStart"
            />
        </div>
    );

    const PlanoContasNavTitle = ({
        title,
        subtitle
    }: {
        title: string;
        subtitle?: string;
    }) => (
        <div className={styles.navTitle}>
            <Stack spacing={8} alignItems="center">
                <h4 style={{ margin: 0 }}>{title}</h4>
                {subtitle ? <Tag>{subtitle}</Tag> : null}
                {dirty ? <Tag color="orange">Alterações não salvas</Tag> : null}
            </Stack>

            {/* <SelectPicker
                data={companiesSelectData}
                value={selectedCompany}
                onChange={(v) => handleChangeCompany(v)}
                placeholder="Selecione uma Empresa"
                placement="leftStart"
            /> */}
        </div>
    );

    const canDeleteRow = (row: DeParaRow) => !isPersisted(row.id);

    const showActionsColumn = Array.isArray(data?.[active])
        ? data[active].some(canDeleteRow)
        : false;

    return (
        <>
            <div
                style={{ marginLeft: localStorage.getItem("hiddenSidebar") === "true" ? "0px" : "203px" }}
                className={classNames("container")}
            >
                <Panel header={<NavTitle title="De/Para • Integração Omie" />} bordered shaded className="mt-3 mb-3">
                    {selectedCompany ? (
                        <>
                            <Stack justifyContent="space-between" alignItems="center" className="">
                                <Nav appearance="tabs" activeKey={active} onSelect={(k) => setActive(k as MappingType)}>
                                    <Nav.Item eventKey="documentType">Tipos de Documento</Nav.Item>
                                    <Nav.Item eventKey="paymentMethod">Formas de Pagamento</Nav.Item>
                                    <Nav.Item eventKey="checkingAccount">Contas Correntes</Nav.Item>
                                    <Nav.Item eventKey="project">Projetos</Nav.Item>
                                    {/* <Nav.Item eventKey="chartOfAccounts">Plano de Contas</Nav.Item> */}
                                    <Nav.Item eventKey="costCenter">Centro de Custos</Nav.Item>
                                </Nav>
                            </Stack>

                            <Table
                                data={data[active]}
                                rowKey="id"
                                bordered
                                cellBordered
                                wordWrap
                                virtualized={false}
                                height={400}
                                loading={loading}
                            >
                                <Column width={90} align="center" fixed>
                                    <HeaderCell>Ativo</HeaderCell>
                                    <Cell>
                                        {(rowData: DeParaRow) => (
                                            <Toggle
                                                size="sm"
                                                checked={rowData.enabled}
                                                onChange={(checked) => handleChangeRow(rowData.id, { enabled: checked })}
                                            />
                                        )}
                                    </Cell>
                                </Column>

                                <Column flexGrow={2}>
                                    <HeaderCell>De (Planilha de Importação)</HeaderCell>
                                    <Cell>
                                        {(rowData: DeParaRow) => (
                                            <Input
                                                size="sm"
                                                value={rowData.omieName}
                                                onChange={(v) => handleChangeRow(rowData.id, { omieName: v })}
                                            />
                                        )}
                                    </Cell>
                                </Column>

                                {active !== "paymentMethod" && (
                                    <Column flexGrow={2}>
                                        <HeaderCell>Para (Base Omie)</HeaderCell>
                                        <Cell>
                                            {(rowData: DeParaRow) => (
                                                <SelectPicker
                                                    key={active}
                                                    data={options}
                                                    value={rowData.internalValue ?? null}
                                                    onChange={(v) => handleChangeRow(rowData.id, { internalValue: v ?? null })}
                                                    placeholder="Selecione…"
                                                    cleanable
                                                    searchable
                                                    style={{ width: "100%" }}
                                                    size="sm"
                                                />
                                            )}
                                        </Cell>
                                    </Column>
                                )}

                                {active === "paymentMethod" && (
                                    <>
                                        <Column flexGrow={2}>
                                            <HeaderCell>Para (Forma de Pagamento)</HeaderCell>
                                            <Cell>
                                                {(rowData: DeParaRow) => (
                                                    <SelectPicker
                                                        key={active}
                                                        data={[
                                                            { value: "PIX", label: "Transferência via PIX por QR Code" },
                                                            { value: "TRA", label: "Transferência Bancária" },
                                                            { value: "BOL", label: "Pagamento de Boleto" },
                                                        ]}
                                                        value={rowData.paymentMethod ?? null}
                                                        onChange={(v) => handleChangeRow(rowData.id, { paymentMethod: v ?? undefined }, true)}
                                                        placeholder="Selecione…"
                                                        cleanable
                                                        searchable
                                                        style={{ width: "100%" }}
                                                        size="sm"
                                                    />
                                                )}
                                            </Cell>
                                        </Column>

                                        <Column flexGrow={2}>
                                            <HeaderCell>Para (Finalidade)</HeaderCell>
                                            <Cell>
                                                {(rowData: DeParaRow) => (
                                                    <SelectPicker
                                                        key={active}
                                                        data={options}
                                                        value={rowData.internalValue ?? null}
                                                        onChange={(v) => handleChangeRow(rowData.id, { internalValue: v ?? null })}
                                                        placeholder="Selecione…"
                                                        cleanable
                                                        searchable
                                                        style={{ width: "100%" }}
                                                        size="sm"
                                                        disabled={rowData.paymentMethod !== "TRA"}
                                                    />
                                                )}
                                            </Cell>
                                        </Column>
                                    </>

                                )}

                                <Column flexGrow={2}>
                                    <HeaderCell>
                                        Observação
                                        <Whisper
                                            speaker={
                                                <Popover>
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: "A anotação feita neste campo não será enviada para o Omie",
                                                        }}
                                                    ></div>
                                                </Popover>
                                            }
                                            trigger={"hover"}
                                            placement="top"
                                        >
                                            <i className="fas fa-info-circle ml-2"></i>
                                        </Whisper>
                                    </HeaderCell>
                                    <Cell>
                                        {(rowData: DeParaRow) => (
                                            <Input
                                                size="sm"
                                                value={rowData.note ?? ""}
                                                onChange={(v) => handleChangeRow(rowData.id, { note: v })}
                                                placeholder="Opcional"
                                            />
                                        )}
                                    </Cell>
                                </Column>

                                {showActionsColumn && (
                                    <Column width={75} align="center" fixed="right">
                                        <HeaderCell>Ações</HeaderCell>
                                        <Cell>
                                            {(rowData: DeParaRow) => {
                                                const canDelete = canDeleteRow(rowData);
                                                return (
                                                    <ButtonToolbar>
                                                        <Whisper
                                                            placement="top"
                                                            trigger="hover"
                                                            speaker={
                                                                <Tooltip>
                                                                    {canDelete ? 'Remover' : 'Registro já salvo — não pode remover'}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <span>
                                                                <Button
                                                                    appearance="link"
                                                                    color="red"
                                                                    disabled={!canDelete}
                                                                    onClick={() => canDelete && handleRemoveRow(rowData.id)}
                                                                >
                                                                    <i className="fas fa-trash" />
                                                                </Button>
                                                            </span>
                                                        </Whisper>
                                                    </ButtonToolbar>
                                                );
                                            }}
                                        </Cell>
                                    </Column>
                                )}
                            </Table>

                            <Stack justifyContent="space-between" alignItems="center" className="mt-3">
                                <div>
                                    <Tag>{data[active]?.length}</Tag> {data[active]?.length === 1 ? 'item exibido' : 'itens exibidos'}
                                </div>
                                <Stack spacing={8}>
                                    <Button appearance="default" onClick={handleAddRow}>
                                        Adicionar item
                                    </Button>
                                    <Button appearance="primary" onClick={handleSave} disabled={!dirty} loading={loading}>
                                        Salvar alterações
                                    </Button>
                                </Stack>
                            </Stack>
                        </>
                    ) : (
                        <NoCompanySelected
                            isLoading={false}
                        />
                    )}
                </Panel>

                <Panel header={<PlanoContasNavTitle title="De/Para • Plano de Contas" />} bordered shaded className="mt-3" style={{ marginBottom: "1rem" }}>
                    <>
                        <Stack justifyContent="space-between" alignItems="center" className="">
                            <Nav appearance="tabs" activeKey={activeCoA}>
                                <Nav.Item eventKey="chartOfAccounts">Plano de Contas</Nav.Item>
                            </Nav>
                        </Stack>

                        <Table
                            data={dataCoA}
                            rowKey="id"
                            bordered
                            cellBordered
                            wordWrap
                            virtualized={false}
                            height={400}
                            loading={loadingCoA}
                        >
                            <Column width={90} align="center" fixed>
                                <HeaderCell>Ativo</HeaderCell>
                                <Cell>
                                    {(rowData: DeParaRow) => (
                                        <Toggle
                                            size="sm"
                                            checked={rowData.enabled}
                                            onChange={(checked) => handleChangeRowCoA(rowData.id, { enabled: checked })}
                                        />
                                    )}
                                </Cell>
                            </Column>

                            <Column flexGrow={2}>
                                <HeaderCell>De (Planilha de Importação)</HeaderCell>
                                <Cell>
                                    {(rowData: DeParaRow) => (
                                        <Input
                                            size="sm"
                                            value={rowData.omieName}
                                            onChange={(v) => handleChangeRowCoA(rowData.id, { omieName: v })}
                                        />
                                    )}
                                </Cell>
                            </Column>

                            <Column flexGrow={2}>
                                <HeaderCell>Para (Base Omie)</HeaderCell>
                                <Cell>
                                    {(rowData: DeParaRow) => (
                                        <SelectPicker
                                            key="chartOfAccounts"
                                            data={optionsCoA}
                                            value={rowData.internalValue ?? null}
                                            onChange={(v) => handleChangeRowCoA(rowData.id, { internalValue: v ?? null })}
                                            placeholder="Selecione…"
                                            cleanable
                                            searchable
                                            style={{ width: "100%" }}
                                            size="sm"
                                        />
                                    )}
                                </Cell>
                            </Column>

                            <Column flexGrow={2}>
                                <HeaderCell>
                                    Observação
                                    <Whisper
                                        speaker={
                                            <Popover>
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: "A anotação feita neste campo não será enviada para o Omie",
                                                    }}
                                                ></div>
                                            </Popover>
                                        }
                                        trigger={"hover"}
                                        placement="top"
                                    >
                                        <i className="fas fa-info-circle ml-2"></i>
                                    </Whisper>
                                </HeaderCell>
                                <Cell>
                                    {(rowData: DeParaRow) => (
                                        <Input
                                            size="sm"
                                            value={rowData.note ?? ""}
                                            onChange={(v) => handleChangeRowCoA(rowData.id, { note: v })}
                                            placeholder="Opcional"
                                        />
                                    )}
                                </Cell>
                            </Column>

                            {showActionsColumnCoA && (
                                <Column width={75} align="center" fixed="right">
                                    <HeaderCell>Ações</HeaderCell>
                                    <Cell>
                                        {(rowData: DeParaRow) => {
                                            const canDelete = canDeleteRowCoA(rowData);
                                            return (
                                                <ButtonToolbar>
                                                    <Whisper
                                                        placement="top"
                                                        trigger="hover"
                                                        speaker={
                                                            <Tooltip>
                                                                {canDelete ? 'Remover' : 'Registro já salvo — não pode remover'}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span>
                                                            <Button
                                                                appearance="link"
                                                                color="red"
                                                                disabled={!canDelete}
                                                                onClick={() => canDelete && handleRemoveRowCoA(rowData.id)}
                                                            >
                                                                <i className="fas fa-trash" />
                                                            </Button>
                                                        </span>
                                                    </Whisper>
                                                </ButtonToolbar>
                                            );
                                        }}
                                    </Cell>
                                </Column>
                            )}
                        </Table>

                        <Stack justifyContent="space-between" alignItems="center" className="mt-3">
                            <div>
                                <Tag>{dataCoA?.length}</Tag> {dataCoA?.length === 1 ? 'item exibido' : 'itens exibidos'}
                            </div>
                            <Stack spacing={8}>
                                <Button appearance="default" onClick={handleAddRowCoA}>
                                    Adicionar item
                                </Button>
                                <Button appearance="primary" onClick={handleSaveCoA} disabled={!dirtyCoA} loading={loadingCoA}>
                                    Salvar alterações
                                </Button>
                            </Stack>
                        </Stack>
                    </>
                </Panel>
            </div>
        </>
    );
}

export default withRouter(RelationPage);